import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Text } from '@factorialco/f0-react'
import { F0Dialog } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { zodResolver } from '@hookform/resolvers/zod'

import Loading from 'modules/core/components/Loading'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FormContent } from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/DeleteBulkModal/FormContent'
import useDestroyByRange from 'modules/shift_management/components/ShiftManagement/DeleteShiftModals/useDestroyByRange'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { Form } from 'modules/shift_management/lib/forms/ShiftsDelete/form'
import { ShiftsDeleteSchemaType } from 'modules/shift_management/lib/forms/ShiftsDelete/schema'

interface Props {
  onClose: () => void
}

const DeleteBulkModal = ({ onClose }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const { defaultValues, schema } = Form()
  const form = useForm<ShiftsDeleteSchemaType>({
    defaultValues,
    resolver: zodResolver(schema),
  })
  const { getValues } = form
  const { startAt, endAt, employeeSelection } = getValues()

  const [destroyBackupShifts, setDestroyBackupShifts] = useState<boolean>(false)
  const [destroyAutomaticOnly, setDestroyAutomaticOnly] = useState<boolean>(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)

  const [{ view }] = useSchedule()
  const { loading, selectableEmployeeIds } = useSelectableEmployees(view.startAt, view.endAt)

  const onCheckboxClick = () => setDestroyBackupShifts(!destroyBackupShifts)

  const employeeCount = employeeSelection.type === 'manual' ? employeeSelection.ids.length : 'all'

  const { onBulkDestroyShifts, submitting } = useDestroyByRange({
    startDate: startAt,
    endDate: endAt,
    employeeSelection,
    destroyBackupShifts,
    destroyAutomaticOnly,
    onClose,
  })

  const featureFlag = hasFeatureFlag('shift_management_rest_day')

  const and = i18n.t('shift_management.add_shift.and')
  const restDaysTitle = `${i18n.t('shift_management.actions.delete_shift')} ${and} ${i18n
    .t('shift_management.sidepanel.rest_days')
    .toLowerCase()}`
  const title = featureFlag ? restDaysTitle : i18n.t('shift_management.delete_shifts')

  const description = featureFlag
    ? i18n.t('shift_management.bulk_delete.rest_days_description')
    : i18n.t('shift_management.bulk_delete.description')
  const button = featureFlag
    ? i18n.t('shift_management.actions.delete')
    : i18n.t('shift_management.delete_shifts')
  const label = featureFlag
    ? i18n.t('shift_management.bulk_delete.rest_days_label')
    : i18n.t('shift_management.bulk_delete.label')

  const disableForm =
    submitting ||
    !startAt ||
    !endAt ||
    (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0)

  if (isConfirmationModalOpen)
    return (
      <F0Dialog
        width="sm"
        onClose={() => {
          setIsConfirmationModalOpen(false)
          onClose()
        }}
        isOpen
        title={i18n.t('shift_management.bulk_delete.confirm_deletion')}
        primaryAction={{
          disabled: submitting,
          label: button,
          onClick: onBulkDestroyShifts,
        }}
        secondaryAction={{
          disabled: submitting,
          label: i18n.t('common.back'),
          onClick: () => setIsConfirmationModalOpen(false),
        }}
      >
        {submitting && (
          <>
            <Spinner />
            <F0Text content={i18n.t('shift_management.actions.deleting_shifts')} variant="body" />
          </>
        )}
        {!submitting && (
          <Box gap="s8">
            {i18n.tView('shift_management.bulk_delete.confirm_deletion_description', {
              start_date: <b>{i18nDateToString(startAt, 'backend')}</b>,
              end_date: <b>{i18nDateToString(endAt, 'backend')}</b>,

              count: (
                <b>
                  {employeeCount}&nbsp;{i18n.t('common.employee_count.other')}
                </b>
              ),
            })}
            <b>{i18n.t('shift_management.bulk_delete.confirm_deletion_warning')}</b>
          </Box>
        )}
      </F0Dialog>
    )

  return (
    <F0Dialog
      width="sm"
      onClose={onClose}
      isOpen
      title={title}
      description={description}
      primaryAction={{
        disabled: disableForm,
        label: button,
        onClick: () => setIsConfirmationModalOpen(true),
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {loading ? (
        <Loading />
      ) : (
        <FormContent
          form={form}
          label={label}
          selectableEmployeeIds={selectableEmployeeIds}
          onCheckboxClick={onCheckboxClick}
          destroyBackUpShifts={destroyBackupShifts}
          setDestroyAutomaticOnly={() => setDestroyAutomaticOnly(!destroyAutomaticOnly)}
          destroyAutomaticOnly={destroyAutomaticOnly}
        />
      )}
    </F0Dialog>
  )
}

export default DeleteBulkModal
